import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LastEditedBy } from '@/components/shared/LastEditedBy';
import { MinimizedSidebar } from '@/components/MinimizedSidebar';
import { ChannelOverviewContent } from '@/components/ChannelOverviewContent';
import ChannelPerformanceContent from '@/components/ChannelPerformanceContent';
import ChannelAnalyticContent from '@/components/ChannelAnalyticContent';
import ChannelActivityContent from '@/components/ChannelActivityContent';
import ChannelCampaignContent from '@/components/ChannelCampaignContent';
import DailyDataContent from '@/components/DailyDataContent';
import DatabaseSegmentationContent from '@/components/DatabaseSegmentationContent';
import ProductInstallationContent from '@/components/ProductInstallationContent';
import ResourceLibraryContent from '@/components/ResourceLibraryContent';
import LogContent from '@/components/LogContent';
import YearlySnapshotPage from '@/pages/YearlySnapshotPage';
import { ChannelSettingsContent } from '@/components/ChannelSettingsContent';
import WarRoomContent from '@/components/WarRoomContent';
import ChannelScorecardContent from '@/components/ChannelScorecardContent';
import ClientTrackerContent from '@/components/ClientTrackerContent';
import CSATContent from '@/components/CSATContent';
import { 
  IBBiographySection, 
  YearlyTargetsSection,
  MonthlyTargetsSection,
  MonthlyMissionsSection,
  HighestRecordSection,
  SocialMediaSection,
  TeamMembersSection,
  BrokerSection
} from '@/components/ChannelProfileSections';
import { 
  ArrowLeft, User, Radio, BarChart3, LineChart, 
  Calendar, FileText, Database, Activity, Target,
  ChevronLeft, ChevronRight, Edit3, Save, X, FolderOpen, Settings, Bell, Search, Crosshair, Package, Users, ClipboardList
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function IBDetailsPage() {
  const { ibId } = useParams();
  const navigate = useNavigate();
  const locationState = window.history.state?.usr || {};
  
  // Core state
  const [ib, setIb] = useState(null);
  const [allIBs, setAllIBs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || locationState?.activeTab || 'profile';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [profileEditMeta, setProfileEditMeta] = useState({});
  
  // Profile data state
  const [profileData, setProfileData] = useState({
    fullName: '',
    nickname: '',
    targetMarket: '',
    tagline: '',
    vision: '',
    mission: '',
    usp: '',
    brandLogoUrl: '',
    personalImageUrl: ''
  });
  
  const [targetsData, setTargetsData] = useState({
    subscribers: '2,500',
    leads: '800',
    closed: '240',
    deposit: '50,000',
    activeClient: 50,
    tradedLot: 100,
    commission: 15000
  });
  
  // Yearly and Monthly Target State - default to current month
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  
  // Yearly targets with same 7 fields as Monthly Targets
  const [yearlyTargets, setYearlyTargets] = useState({
    subscribers: '30,000',
    leads: '9,600',
    closed: '2,880',
    deposit: '600,000',
    activeClient: 600,
    tradedLot: 1200,
    commission: 180000
  });
  
  // Monthly targets stored by month-year key
  const [monthlyTargetsData, setMonthlyTargetsData] = useState({});
  
  // Monthly missions stored by month-year key
  const [monthlyMissionsData, setMonthlyMissionsData] = useState({});
  
  // Historical and daily data for Highest Record calculation
  const [historicalData, setHistoricalData] = useState([]);
  const [dailyData, setDailyData] = useState({ marketing: [], sales: [] });
  
  const getMonthKey = (month, year) => `${year}-${String(month + 1).padStart(2, '0')}`;
  
  const getCurrentMonthTargets = () => {
    const key = getMonthKey(selectedMonth, selectedYear);
    return monthlyTargetsData[key] || targetsData;
  };
  
  const getCurrentMonthMissions = () => {
    const key = getMonthKey(selectedMonth, selectedYear);
    return monthlyMissionsData[key] || targetsData;
  };
  
  const handleMonthChange = (newMonth, newYear) => {
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };
  
  const handleYearChange = (newYear) => {
    setSelectedYear(newYear);
  };
  
  const handleMonthlyTargetsChange = (newData) => {
    const key = getMonthKey(selectedMonth, selectedYear);
    setMonthlyTargetsData(prev => ({ ...prev, [key]: newData }));
    setTargetsData(newData);
  };
  
  const handleMonthlyMissionsChange = (newData) => {
    const key = getMonthKey(selectedMonth, selectedYear);
    setMonthlyMissionsData(prev => ({ ...prev, [key]: newData }));
  };
  
  const [socialPlatforms, setSocialPlatforms] = useState([
    { platform: 'Telegram', link: 'https://t.me/fira_indonesia' },
    { platform: 'Instagram', link: '@fira.indonesia' },
    { platform: 'TikTok', link: '@fira.trading' },
    { platform: 'YouTube', link: 'FIRA Trading' },
    { platform: 'Facebook', link: 'FIRA Indonesia Official' },
    { platform: 'WhatsApp', link: '+62 812 3456 7890' },
  ]);
  
  const [teamMembers, setTeamMembers] = useState([
    { role: 'Project Manager', name: 'Ahmad Zaki' },
    { role: 'Team Leader', name: 'Sarah Lee' },
    { role: 'Content Creator', name: 'Ravi Kumar' },
    { role: 'Signal Provider', name: 'David Tan' },
    { role: 'Admin Sales', name: 'Mike Chen' },
    { role: 'Channel Moderator', name: 'Lisa Wong' },
    { role: 'Video Grapher', name: 'John Lim' },
    { role: 'Graphic Designer', name: 'Maya Singh' },
    { role: 'Marketer', name: 'Ali Hassan' },
    { role: 'Script Writer', name: 'Nina Tan' },
  ]);

  // Navigation tabs configuration
  const allTabs = [
    { id: 'dashboard', label: 'Channel Dashboard', icon: Target },
    { id: 'yearly-snapshot', label: 'Yearly Snapshot', icon: Calendar },
    { id: 'profile', label: 'Channel Profile', icon: User },
    { id: 'overview', label: 'Channel Overview', icon: Radio },
    { id: 'performance', label: 'Channel Performance', icon: BarChart3 },
    { id: 'war-room', label: 'Channel War Room', icon: Crosshair },
    { id: 'analytic', label: 'Channel Analytic', icon: LineChart },
    { id: 'activity', label: 'Channel Activity', icon: Calendar },
    { id: 'campaign', label: 'Channel Campaign', icon: Target },
    { id: 'daily', label: 'Daily Data', icon: Database },
    { id: 'database', label: 'Database Segmentation', icon: Database },
    { id: 'product-install', label: 'Product Installation', icon: Package },
    { id: 'clients', label: 'Client Tracker', icon: Users },
    { id: 'csat', label: 'CSAT Survey', icon: ClipboardList },
    { id: 'resources', label: 'Resource Library', icon: FolderOpen },
    { id: 'settings', label: 'Channel Settings', icon: Settings },
    { id: 'log', label: 'Log', icon: Activity },
  ];
  
  // Filter tabs based on user permissions
  const userPerms = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').permissions || {}; } catch { return {}; } })();
  const userRole = (() => { try { return (JSON.parse(localStorage.getItem('user') || '{}').role_id || '').toLowerCase(); } catch { return ''; } })();
  const hasSettingsPerm = userPerms.channel_settings !== false && (userPerms.channel_settings || ['admin','manager','developer'].includes(userRole));
  const tabs = allTabs.filter(t => {
    if (t.id === 'settings' && !hasSettingsPerm) return false;
    return true;
  });

  // Fetch data on mount
  useEffect(() => {
    fetchAllIBs();
    fetchIBData();
    fetchHistoricalData();
    fetchDailyDataFromStorage();
  }, [ibId]);

  // Listen for cross-component tab switch requests (e.g. CSAT card in scorecard)
  useEffect(() => {
    const handler = (e) => {
      const t = e?.detail?.tab;
      if (t) setActiveTab(t);
    };
    window.addEventListener('ib:switch-tab', handler);
    return () => window.removeEventListener('ib:switch-tab', handler);
  }, []);
  
  const fetchHistoricalData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Fetch 2025 historical data
      const response = await axios.get(`${API}/historical/2025/${ibId}/summary?start_date=2025-01-01&end_date=2025-12-31`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.daily_data) {
        setHistoricalData(response.data.daily_data);
      }
    } catch (error) {
      console.error('Failed to load historical data:', error);
    }
  };
  
  const fetchDailyDataFromStorage = () => {
    try {
      // Load 2026 data from localStorage
      const marketingData = localStorage.getItem(`marketing_data_${ibId}`);
      const salesData = localStorage.getItem(`sales_data_${ibId}`);
      
      const data = {
        marketing: marketingData ? JSON.parse(marketingData) : [],
        sales: salesData ? JSON.parse(salesData) : []
      };
      
      setDailyData(data);
    } catch (error) {
      console.error('Failed to load daily data from storage:', error);
    }
  };
  
  // Fetch Channel Targets (Monthly/Yearly Targets & Missions) from API
  const fetchChannelTargets = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !ibId) return;
      
      const currentYear = new Date().getFullYear();
      const response = await axios.get(`${API}/channel-targets/${ibId}/${currentYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        // Load Monthly Targets
        if (response.data.monthly) {
          const monthlyData = {};
          Object.entries(response.data.monthly).forEach(([key, value]) => {
            monthlyData[key] = value;
          });
          if (Object.keys(monthlyData).length > 0) {
            setMonthlyTargetsData(monthlyData);
          }
        }
        
        // Load Monthly Missions
        if (response.data.monthly_missions) {
          const missionsData = {};
          Object.entries(response.data.monthly_missions).forEach(([key, value]) => {
            missionsData[key] = { missions: value };
          });
          if (Object.keys(missionsData).length > 0) {
            setMonthlyMissionsData(missionsData);
          }
        }
        
        // Load Yearly Targets
        if (response.data.yearly) {
          setYearlyTargets(prev => ({ ...prev, ...response.data.yearly }));
        }
        
        console.log('[Channel Targets] Loaded from API');
      }
    } catch (error) {
      console.log('[Channel Targets] Using localStorage fallback');
    }
  };
  
  // Fetch channel targets on mount and when settings are updated
  useEffect(() => {
    fetchChannelTargets();
    
    // Listen for channel targets update event (from Channel Settings)
    const handleTargetsUpdated = () => {
      console.log('[IBDetailsPage] Received channelTargetsUpdated event, refreshing...');
      fetchChannelTargets();
    };
    
    window.addEventListener('channelTargetsUpdated', handleTargetsUpdated);
    
    return () => {
      window.removeEventListener('channelTargetsUpdated', handleTargetsUpdated);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ibId]);

  const fetchAllIBs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/ib/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllIBs(response.data);
    } catch (error) {
      console.error('Failed to load IB list');
    }
  };

  const fetchIBData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const ibResponse = await axios.get(`${API}/ib/${ibId}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });

      setIb(ibResponse.data);
      
      // Get active brokers from IB data
      const activeBrokers = ibResponse.data.active_brokers || ['octa', 'hfm', 'vantage'];
      
      // Load saved profile from MongoDB
      const profileRes = await axios.get(`${API}/ib-profile/${ibId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
      const savedProfile = profileRes?.data?.data;
      if (savedProfile?.updated_by_name) {
        setProfileEditMeta({ name: savedProfile.updated_by_name, time: savedProfile.updated_at });
      }
      
      if (savedProfile && savedProfile.profileData) {
        // Use saved brokers if they exist, otherwise use defaults from API
        const savedBrokers = savedProfile.profileData.brokers;
        setProfileData({
          ...savedProfile.profileData,
          brokers: (savedBrokers && savedBrokers.length > 0) ? savedBrokers : activeBrokers
        });
        setTargetsData(savedProfile.targetsData || {
          subscribers: '2,500', leads: '800', closed: '240',
          deposit: ibResponse.data.monthly_target_deposit || '50,000',
          activeClient: ibResponse.data.monthly_target_active_client || 50,
          tradedLot: ibResponse.data.monthly_target_lot || 100,
          commission: ibResponse.data.monthly_target_commission || 15000
        });
        setSocialPlatforms(savedProfile.socialPlatforms || []);
        setTeamMembers(savedProfile.teamMembers || []);
      } else {
        // Initialize with API data
        setProfileData({
          fullName: ibResponse.data.name,
          nickname: 'FIRA Indonesia',
          targetMarket: 'Indonesian Forex Traders',
          tagline: 'Trading Made Simple, Profits Made Real',
          vision: 'To become the leading IB in Indonesia, empowering traders with education and profitable trading strategies.',
          mission: 'Provide high-quality trading signals, comprehensive education, and dedicated support to help traders achieve consistent profitability.',
          usp: '85% win rate signals • 24/7 support • Live trading sessions • VIP mentorship program',
          brandLogoUrl: '',
          personalImageUrl: '',
          brokers: activeBrokers
        });
        
        setTargetsData({
          subscribers: '2,500',
          leads: '800',
          closed: '240',
          deposit: ibResponse.data.monthly_target_deposit || '50,000',
          activeClient: ibResponse.data.monthly_target_active_client || 50,
          tradedLot: ibResponse.data.monthly_target_lot || 100,
          commission: ibResponse.data.monthly_target_commission || 15000
        });
      }
    } catch (error) {
      toast.error('Failed to load IB data');
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/ib-profile/${ibId}`, {
        profileData,
        targetsData,
        socialPlatforms,
        teamMembers
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Profile saved successfully!');
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to save profile');
    }
  };
  
  const handleCancelEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const profileRes = await axios.get(`${API}/ib-profile/${ibId}`, { headers: { Authorization: `Bearer ${token}` } });
      const savedProfile = profileRes?.data?.data;
      if (savedProfile && savedProfile.profileData) {
        setProfileData(savedProfile.profileData);
        setTargetsData(savedProfile.targetsData);
        setSocialPlatforms(savedProfile.socialPlatforms || []);
        setTeamMembers(savedProfile.teamMembers || []);
      }
    } catch {}
    setEditMode(false);
    toast.info('Changes cancelled');
  };
  
  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await axios.post(`${API}/upload/image`, formData, { headers: { Authorization: `Bearer ${token}` } });
        const imageUrl = uploadRes.data.url;
        
        const newData = {...profileData};
        if (type === 'brand') {
          newData.brandLogoUrl = imageUrl;
        } else {
          newData.personalImageUrl = imageUrl;
        }
        setProfileData(newData);
        
        // Save to MongoDB immediately
        await axios.put(`${API}/ib-profile/${ibId}`, {
          profileData: newData, targetsData, socialPlatforms, teamMembers
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Image uploaded & saved!');
      } catch {
        toast.error('Failed to upload image');
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#09090B]">
        <MinimizedSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[hsl(var(--brand-red))] border-t-transparent rounded-full animate-spin" />
            <p className="text-[hsl(var(--text-muted))]">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!ib) return null;

  return (
    <div className="flex min-h-screen bg-[#09090B] text-[hsl(var(--text-primary))]">
      <MinimizedSidebar />
      
      {/* Secondary Sidebar - Sub-module navigation */}
      <aside className={`
        ${sidebarCollapsed ? 'w-16' : 'w-60'} 
        bg-[hsl(var(--surface))] border-r border-[hsl(var(--border))]
        fixed left-16 top-0 bottom-0 z-40 
        transition-all duration-200 ease-out
        flex flex-col
        hidden md:flex
      `}>
        {/* Sidebar Header */}
        <div className={`p-4 border-b border-[hsl(var(--border))] ${sidebarCollapsed ? 'px-2' : ''}`}>
          {!sidebarCollapsed ? (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/ib-management/list')}
                className="h-8 w-8 shrink-0 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] hover:bg-[hsl(var(--surface-elevated))]"
              >
                <ArrowLeft size={16} />
              </Button>
              <Select value={ibId} onValueChange={(value) => navigate(`/ib/${value}/details`)}>
                <SelectTrigger className="h-8 flex-1 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-sm font-semibold">
                  <SelectValue>{ib.name}</SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] max-h-[240px] overflow-y-auto">
                  {allIBs.filter(item => item.status === 'active').map(item => (
                    <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarCollapsed(true)}
                className="h-8 w-8 shrink-0 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]"
              >
                <ChevronLeft size={16} />
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/ib-management/list')}
                className="h-10 w-10 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]"
              >
                <ArrowLeft size={18} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarCollapsed(false)}
                className="h-8 w-8 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <nav className={`flex-1 py-4 overflow-y-auto ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
          <div className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    w-full flex items-center gap-3 rounded-lg transition-all duration-150
                    ${sidebarCollapsed ? 'h-10 justify-center' : 'px-3 py-2'}
                    ${isActive 
                      ? 'bg-[hsl(var(--brand-red)/0.15)] text-[hsl(var(--brand-red))] border border-[hsl(var(--brand-red)/0.3)]' 
                      : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--surface-elevated))] hover:text-[hsl(var(--text-primary))]'
                    }
                  `}
                  title={sidebarCollapsed ? tab.label : undefined}
                >
                  <Icon size={sidebarCollapsed ? 18 : 16} />
                  {!sidebarCollapsed && <span className="text-sm">{tab.label}</span>}
                </button>
              );
            })}
          </div>
        </nav>
      </aside>

      {/* Main Content Area */}
      <main className={`
        ${sidebarCollapsed ? 'md:ml-[128px]' : 'md:ml-[304px]'} 
        flex-1 flex flex-col h-screen overflow-y-auto overflow-x-hidden
        transition-all duration-200 ease-out pt-[44px] md:pt-0
      `}>
        {/* Global Top Bar - Search, Notifications, User */}
        <div className="hidden lg:flex items-center justify-end gap-3 px-6 py-2.5 border-b border-[hsl(var(--border))] bg-[#09090B]/95 backdrop-blur-md sticky top-0 z-50">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="w-9 h-9 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] hover:border-[hsl(var(--primary))]/30 transition-all" title="Dashboard">
              <Search size={16} />
            </button>
            <div className="relative">
              <button className="w-9 h-9 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] hover:border-[hsl(var(--primary))]/30 transition-all">
                <Bell size={16} />
              </button>
            </div>
            <div className="flex items-center gap-2.5 pl-2 border-l border-[hsl(var(--border))]">
              {(() => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.avatar_url ? <img src={u.avatar_url} alt="" className="w-8 h-8 rounded-lg object-cover" /> : <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--brand-orange))] flex items-center justify-center text-white text-xs font-bold">{u.name?.charAt(0) || '?'}</div>; } catch { return null; } })()}
              <div className="text-right">
                <p className="text-sm font-medium text-[hsl(var(--text-primary))] leading-tight">{(() => { try { return JSON.parse(localStorage.getItem('user') || '{}').name || 'User'; } catch { return 'User'; } })()}</p>
                <p className="text-[10px] text-[hsl(var(--text-muted))] leading-tight">{(() => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.department || u.position || u.role_id || ''; } catch { return ''; } })()}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile Tab Navigation - FIXED for iOS compatibility */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#09090B] border-b border-[hsl(var(--border))] overflow-x-auto" style={{WebkitOverflowScrolling: 'touch'}}>
          <div className="flex items-center gap-1 px-2 py-2 min-w-max">
            <Button variant="ghost" size="sm" onClick={() => navigate('/ib-management/list')} className="shrink-0 h-8 w-8 p-0"><ArrowLeft size={16} /></Button>
            <span className="text-xs font-bold shrink-0 mr-1 max-w-[60px] truncate">{ib.name}</span>
            {[
              { key: 'profile', label: 'Profile' },
              { key: 'overview', label: 'Overview' },
              { key: 'performance', label: 'Perf' },
              { key: 'war-room', label: 'War Room' },
              { key: 'analytic', label: 'Analytic' },
              { key: 'activity', label: 'Activity' },
              { key: 'campaign', label: 'Campaign' },
              { key: 'daily', label: 'Data' },
              ...(hasSettingsPerm ? [{ key: 'settings', label: 'Settings' }] : []),
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`shrink-0 px-3 py-1.5 rounded-md text-[10px] font-medium whitespace-nowrap ${activeTab === tab.key ? 'bg-[hsl(var(--primary))] text-white' : 'text-[hsl(var(--text-muted))] bg-[hsl(var(--surface))]'}`}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Channel Profile Tab */}
        {activeTab === 'profile' && (
          <>
            {/* Sticky Header */}
            <header className="md:sticky md:top-0 z-20 bg-[#09090B]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
              <div className="px-4 sm:px-8 py-3 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold font-heading tracking-tight">Channel Profile</h1>
                  <p className="text-xs sm:text-sm text-[hsl(var(--text-muted))]">IB Identity & Information</p>
                  <LastEditedBy name={profileEditMeta.name} timestamp={profileEditMeta.time} resourceType="ib-profile" resourceId={ibId} pageName="Channel Profile" />
                </div>
                <div className="flex items-center gap-3">
                  <Badge className="bg-[hsl(var(--emerald)/0.15)] text-[hsl(var(--emerald))] border border-[hsl(var(--emerald)/0.3)] px-3 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--emerald))] mr-2 animate-pulse" />
                    ACTIVE
                  </Badge>
                  {!editMode ? (
                    <Button
                      onClick={() => setEditMode(true)}
                      className="bg-[hsl(var(--brand-red))] hover:bg-[hsl(var(--brand-red-light))] text-white"
                    >
                      <Edit3 size={16} className="mr-2" />
                      Edit Profile
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveProfile}
                        className="bg-[hsl(var(--emerald))] hover:bg-[hsl(var(--emerald)/0.9)] text-black"
                      >
                        <Save size={16} className="mr-2" />
                        Save
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleCancelEdit}
                        className="border-[hsl(var(--border-hover))] text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--surface))]"
                      >
                        <X size={16} className="mr-2" />
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </header>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-4 sm:px-8 py-4 sm:py-6 space-y-6 max-w-[1600px]">
                <IBBiographySection 
                  data={profileData}
                  editMode={editMode}
                  onDataChange={setProfileData}
                  onImageUpload={handleImageUpload}
                />
                
                <BrokerSection 
                  data={profileData}
                  editMode={editMode}
                  onDataChange={setProfileData}
                />
                
                {/* Yearly Targets Section - NEW */}
                <YearlyTargetsSection 
                  data={yearlyTargets}
                  editMode={editMode}
                  onDataChange={setYearlyTargets}
                  selectedYear={selectedYear}
                  onYearChange={handleYearChange}
                  channelId={ibId}
                />
                
                {/* Monthly Targets Section with Month Navigation */}
                <MonthlyTargetsSection 
                  data={getCurrentMonthTargets()}
                  editMode={editMode}
                  onDataChange={handleMonthlyTargetsChange}
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onMonthChange={handleMonthChange}
                  yearlyTargets={yearlyTargets}
                  channelId={ibId}
                />
                
                {/* Monthly Missions Section with Month Navigation */}
                <MonthlyMissionsSection 
                  data={getCurrentMonthMissions()}
                  editMode={editMode}
                  onDataChange={handleMonthlyMissionsChange}
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onMonthChange={handleMonthChange}
                  channelId={ibId}
                />
                
                <HighestRecordSection 
                  data={{}} 
                  historicalData={historicalData}
                  dailyData={dailyData}
                />
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <SocialMediaSection 
                    platforms={socialPlatforms}
                    editMode={editMode}
                    onPlatformsChange={setSocialPlatforms}
                  />
                  <TeamMembersSection 
                    members={teamMembers}
                    editMode={editMode}
                    onMembersChange={setTeamMembers}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Channel Overview Tab */}
        {activeTab === 'overview' && (
          <ChannelOverviewContent ib={ib} />
        )}

        {/* Channel Performance Tab */}
        {activeTab === 'performance' && (
          <ChannelPerformanceContent ib={ib} />
        )}

        {/* Placeholder Tabs */}
        {activeTab === 'war-room' && <WarRoomContent ib={ib} />}
        {activeTab === 'analytic' && <ChannelAnalyticContent ib={ib} />}
        {activeTab === 'activity' && <ChannelActivityContent ib={ib} />}
        {activeTab === 'campaign' && <ChannelCampaignContent ib={ib} />}
        {activeTab === 'daily' && <DailyDataContent ib={ib} />}
        {activeTab === 'database' && <DatabaseSegmentationContent ib={ib} />}
        {activeTab === 'product-install' && <ProductInstallationContent ib={ib} />}
        {activeTab === 'clients' && <ClientTrackerContent ib={ib} />}
        {activeTab === 'csat' && <CSATContent ib={ib} />}
        {activeTab === 'resources' && <ResourceLibraryContent ib={ib} />}
        {activeTab === 'settings' && (() => {
          const u = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
          const p = u.permissions || {};
          const r = (u.role_id || '').toLowerCase();
          const canAccess = p.channel_settings !== false && (p.channel_settings || ['admin','manager','developer'].includes(r));
          if (!canAccess) return <div className="text-center py-20"><p className="text-[hsl(var(--text-muted))]">Access Denied — Channel Settings restricted</p></div>;
          return <ChannelSettingsContent ib={ib} />;
        })()}
        {activeTab === 'log' && <LogContent key={ib?.id} ib={ib} />}
        {activeTab === 'yearly-snapshot' && <YearlySnapshotPage ibIdFilter={ib?.id} ibName={ib?.name} embedded />}
        {activeTab === 'dashboard' && <ChannelScorecardContent ib={ib} />}
      </main>
    </div>
  );
}

// Placeholder component for unbuilt tabs
function PlaceholderTab({ title, description, variant = 'default' }) {
  const variants = {
    default: 'border-[hsl(var(--border))]',
    'coming-soon': 'border-[hsl(var(--gold)/0.3)] bg-[hsl(var(--gold)/0.05)]',
    'important': 'border-[hsl(var(--brand-red)/0.3)] bg-[hsl(var(--brand-red)/0.05)]',
    'restricted': 'border-[hsl(var(--gold)/0.3)] bg-[hsl(var(--gold)/0.05)]',
  };

  return (
    <div className="flex-1 p-8">
      <div className={`border rounded-xl p-8 ${variants[variant]}`}>
        <h2 className="text-xl font-semibold font-heading mb-2">{title}</h2>
        <p className="text-[hsl(var(--text-muted))]">{description}</p>
        {variant === 'coming-soon' && (
          <Badge className="mt-4 bg-[hsl(var(--gold)/0.15)] text-[hsl(var(--gold))] border-[hsl(var(--gold)/0.3)]">
            Coming Soon
          </Badge>
        )}
        {variant === 'important' && (
          <p className="mt-4 text-sm text-[hsl(var(--brand-red))]">
            ⚠️ Mandatory daily submission
          </p>
        )}
        {variant === 'restricted' && (
          <p className="mt-4 text-sm text-[hsl(var(--gold))]">
            🔒 Management & Admin Sales Only
          </p>
        )}
      </div>
    </div>
  );
}
